import { z } from "zod";
import type {
  StepCategory,
  StepLibraryModule,
  WorkflowStepBatchConfig,
} from "../types";
import { type SystemEvent } from "@erp/shared-types";
import { type SystemEventData } from "../events/system-event-schemas";
import type { Permission } from "@erp/authz";
import type { ExecutionLifecycle } from "../execution-lifecycle";
import type { WorkflowGuardrail, GuardrailContext } from "../utils/guardrails";
import type { VisibilityRule } from "./visibility";

// Re-export types needed by step implementations
export type { StepCategory, StepLibraryModule };

/**
 * A cell value as a data-mapping row carries it at runtime — the JS-value
 * union, not the schema-type vocabulary (mirrors the backend's
 * `StatefulTableCellValue`).
 *
 * A mapping is a `sql`-type schema, restricted to the six 1:1-Postgres
 * column types (string, integer, float, numeric, boolean, date) — the richer
 * extraction-schema vocabulary (json, object, enum, …) never reaches one.
 * Read back through the driver those six collapse to four JS shapes:
 * integer/float → `number`; numeric → `string` (precision-preserving);
 * date → ISO `string`; boolean → `boolean`; SQL NULL → `null`. The column's
 * declared type stays available on the handle's `columns[].type`.
 */
export type DataMappingCellValue = string | number | boolean | null;

/**
 * An in-memory, activity-lifetime handle onto one data mapping's rows.
 *
 * Obtained through {@link StepExecutionContext.dataMappings}. Loading happens
 * once per activity (all items of a batched chunk share it); every method is
 * synchronous against lazily built hash indexes, so it is safe to call inside
 * a tight per-item loop.
 */
export interface DataMappingHandle {
  slug: string;
  /** The mapping's live columns, in schema order. */
  columns: ReadonlyArray<{ name: string; type: string }>;
  /** The declared key columns, in key order. */
  keyColumns: readonly string[];
  /** Column sets known unique: the key plus the mapping's unique indexes. */
  uniqueSets: ReadonlyArray<readonly string[]>;
  rowCount: number;
  /**
   * Every row whose columns equal the given values (AND-joined). O(1) after
   * the first call per column set. Rows with a null in any queried column
   * never match, mirroring the SQL path.
   */
  match(
    values: Readonly<Record<string, string | number | boolean>>,
  ): Array<Record<string, DataMappingCellValue>>;
  /**
   * The single row addressed by a unique column set. The keys of `values`
   * must form one of {@link uniqueSets}; throws otherwise, and throws when
   * the data turns out to hold duplicates for a supposedly unique set.
   */
  findByUniqueIndex(
    values: Readonly<Record<string, string | number | boolean>>,
  ): Record<string, DataMappingCellValue> | undefined;
}

/**
 * Lazy, activity-cached access to the organization's data mappings. The first
 * `get` per mapping loads and indexes its rows; subsequent calls (including
 * from other items of the same batched chunk) reuse the handle.
 */
export interface WorkflowDataMappings {
  /** Resolve by the mapping's stable, code-facing slug. */
  get(slug: string): Promise<DataMappingHandle>;
}

/**
 * Workflow execution context provided to steps during execution
 */
export interface StepExecutionContext<
  TEmits extends readonly SystemEvent[] = readonly SystemEvent[],
> {
  workflowRunId: string;
  workflowDefinitionId?: string;
  /**
   * Lineage workflow id (`wfv2_workflows.id`) — stable across versions, unlike
   * `workflowDefinitionId` which changes on every finalized edit. Use this to
   * key data that must survive version bumps (e.g. workflow-scoped memories).
   */
  workflowId?: string;
  stepRunId: string;
  organizationId: string;
  userId: string | null; // null for system-triggered workflows

  traceId: string;

  logger: Pick<Console, "log" | "info" | "warn" | "error" | "debug">;

  /**
   * Per-step lifecycle handle: cancellation state + signal, `cancel()` for
   * handler-initiated stops, `checkCancelled()` to bail at a safe point,
   * and `sendHeartbeat()` / `sendProgressHeartbeat(details)` for liveness
   * and progress observability. See `ExecutionLifecycle` for per-method
   * guidance.
   */
  lifecycle: ExecutionLifecycle;

  // Function to emit system events (typed based on step's emits configuration)
  // Events are validated against registered schemas and published to RabbitMQ
  emit: <E extends TEmits[number]>(
    eventType: E,
    data: SystemEventData[E],
  ) => Promise<void>;

  // Temporary compatibility field for schedule trigger migrations
  scheduledTime?: string;

  /**
   * Set to `true` when the step is being executed from the test-suite eval
   * runner. Handlers that have observable side-effects (DB writes, external
   * API calls that cost money or pollute production data) should branch on
   * this flag to skip those writes and return any necessary state inline on
   * the step output so the evaluator can read it without DB hydration.
   *
   * Defaults to undefined / falsy in production. Treat absence as "normal
   * execution" so existing handlers that don't know about this flag keep
   * working unchanged.
   */
  isEvalRun?: boolean;

  /**
   * Execution mode for this step.
   *
   * - `"run"` (default): normal first-time execution within a new workflow run.
   * - `"rerun"`: the step is being actively re-executed as part of a rerun.
   *   Handlers that cache or reuse prior outputs (e.g. document splits) should
   *   bypass the cache and redo the work.
   *
   * Kept as a string union (not boolean) so additional modes can be added
   * without breaking existing checks (e.g. `"force-rerun"`).
   *
   * Absent / undefined is equivalent to `"run"`.
   */
  runMode?: "run" | "rerun";

  /**
   * Raw step input payload provided by the workflow orchestrator before
   * schema validation/normalization. Useful for handlers that need broader
   * execution context than the typed input sockets expose.
   */
  workflowInputData?: Record<string, unknown>;

  /** Set when executing within a config-driven agent run. */
  agentRunId?: string;

  /**
   * The background agent acting, when there is no agent run to find it from:
   * an App's tool calls run as its agent (see `backend/src/core/services/apps`).
   */
  agentDefinitionId?: string;

  /**
   * First-writer-wins memo for the enclosing agent run: returns the value
   * already stored under `name`, or stores and returns `candidate`. Survives a
   * resumed attempt of the same run, so a tool that writes can store its own
   * idempotency key (e.g. the id of the record it created) and reuse it.
   * Present only when executing as a config-driven agent's tool.
   */
  memo?: <T>(name: string, candidate: T) => Promise<T>;

  /** Guardrails loaded for this step (DB or inline config). Undefined if step has no guardrails. */
  guardrails?: WorkflowGuardrail[];
  /** Pre-built guardrail context for handler use in runGuardedOperation calls. */
  guardrailContext?: GuardrailContext;

  /**
   * Signal payload from the waitForSignal loop. Present on every iteration:
   * the initial run gets a bootstrap payload with signalName "__initial__",
   * subsequent runs get the actual signal data (e.g., triggeredByUserId).
   */
  signalPayload?: {
    signalName: string;
    payload?: Record<string, unknown>;
  };

  /**
   * User IDs watching the workflow or source entity that triggered this run.
   * Resolved at run start from CoreWatch. Steps can propagate these onto
   * newly created entities (e.g. tasks).
   */
  runWatchers?: string[];

  /**
   * Lazy, activity-cached data-mapping lookups (see
   * {@link WorkflowDataMappings}). Optional so mocked contexts and older
   * callers keep working — handlers that need it should fall back to their
   * per-item read path when absent.
   */
  dataMappings?: WorkflowDataMappings;

  /**
   * ID of the task this agent run is currently executing, when spawned by the
   * orchestrated task loop. Task tools should prefer this over requiring the
   * LLM to supply a task ID explicitly.
   */
  currentTaskId?: string;

  /**
   * Yield to the workflow manager agent for assistance. Only present when
   * a manager agent is configured on the parent Workflow. Each call creates
   * a fresh AgentRun linked to the current WorkflowRun and StepRun.
   */
  yieldToManagerAgent?: (request: {
    reason: string;
    prompt: string;
    context?: Record<string, unknown>;
  }) => Promise<{
    agentRunId: string;
    outputData: Record<string, unknown>;
  } | null>;
}

export type ToolExecutionContext<
  TEmits extends readonly SystemEvent[] = readonly SystemEvent[],
> = StepExecutionContext<TEmits>;

/** @deprecated Use StepExecutionContext */
export type WorkflowExecutionContext<
  TEmits extends readonly SystemEvent[] = readonly SystemEvent[],
> = StepExecutionContext<TEmits>;

/**
 * Union of all registered step type identifiers. Add new entries here when
 * creating a new step — TypeScript will enforce that `StepConfig.type` and
 * `deprecated.replacedBy` are members of this union.
 */
export type StepTypeId =
  | "action_input"
  | "action_output"
  | "ai_reasoning"
  | "verify_step_output"
  | "approval"
  | "archive_ship_and_debit_azure_files"
  | "await_erp_order_numbers"
  | "background_agent"
  | "blob_storage_fetch"
  | "check_storage_write_errors"
  | "code_sandbox"
  | "collect_results"
  | "close_remote_desktop"
  | "classify_document"
  | "classify_document_v2"
  | "classify_email"
  | "classify_master_data"
  | "classify_sd"
  | "compute_request_date"
  | "compare_shipping_document"
  | "confidence_threshold"
  | "convert_e844sd_to_e844d"
  | "core_enum_fanout"
  | "create_ap_invoice_draft"
  | "get_ap_invoice"
  | "update_ap_invoice_field"
  | "decompose_entity"
  | "get_cash_receipt"
  | "classify_bank_memo"
  | "build_cash_receipt_verification_subject"
  | "submit_bank_memo_remittance"
  | "record_deposit_file"
  | "split_deposit_file"
  | "submit_deposit_remittance"
  | "request_remittance_portal_fetch"
  | "get_product_variant"
  | "start_data_source_load"
  | "create_jira_issue"
  | "create_pending_payment"
  | "build_email_remittance_provenance"
  | "submit_portal_remittance_documents"
  | "create_pye_snapshot"
  | "create_sales_order_draft"
  | "create_draft_sales_order"
  | "create_sales_order_draft_from_document"
  | "create_task"
  | "create_typed_task"
  | "resolve_task"
  | "create_subtask"
  | "complete_task"
  | "aws_s3_datasource_adapter"
  | "blob_storage_datasource_adapter"
  | "csv_datasource_writer"
  | "csv_transform_v4"
  | "csv_transform"
  | "data_asset"
  | "fwf_datasource_writer"
  | "decision"
  | "decision_v4"
  | "emit_system_event"
  | "detect_order_issues"
  | "compute_order_facts"
  | "resolve_order_fields"
  | "resolve_shipping_assignee"
  | "resolve_draft_details"
  | "reassign_draft_sales_order"
  | "classify_organization"
  | "submit_draft_sales_orders"
  | "set_discount_group_status"
  | "create_discount_group_from_extraction"
  | "email_rule_trigger"
  | "enqueue_rebate_session"
  | "entity_resolver"
  | "event_trigger"
  | "external_http_call"
  | "extract_ap_invoice_from_document"
  | "extract_ap_invoice_from_email"
  | "extract_document_content"
  | "extract_document_content_v2"
  | "extract_document_content_v3"
  | "extract_document_content_v4"
  | "extract_famous_order_from_email_subject"
  | "extract_remittance_from_document"
  | "famous_sql_query"
  | "fan_in"
  | "fetch_purchase_order_from_famous"
  | "fetch_sales_order_from_famous"
  | "form_trigger"
  | "fetch_transfer_order_from_famous"
  | "filter_email"
  | "filter_email_attachments"
  | "filter_email_by_customer"
  | "fusion_sync"
  | "generate_task"
  | "hello_world"
  | "workbook_transform"
  | "run_workbook"
  | "read_workbook_result"
  | "run_saved_workbook"
  | "list_workbooks"
  | "read_workbook_results"
  | "describe_workbook"
  | "workbook_output_link"
  | "list_workbook_automations"
  | "open_workbook_run_tasks"
  | "http_trigger"
  | "identify_and_filter_sd"
  | "identify_customer"
  | "identify_supplier"
  | "identify_location"
  | "identify_sd_customer"
  | "ingest_ar_data"
  | "join_documents"
  | "ingest_ar_payments"
  | "ingest_edi_translation_lists"
  | "ingest_bank_transactions_from_document"
  | "ingest_microsoft_graph_email"
  | "ingest_sales_load"
  | "interpolate_email_template"
  | "detect_language"
  | "segment_language"
  | "translate_language"
  | "login_to_remote_desktop"
  | "remote_desktop_execute"
  | "log_value"
  | "array_length"
  | "managed_mapper"
  | "map_value"
  | "master_data_bulk_insert"
  | "memory_bank_store"
  | "memory_bank_retrieve"
  | "match_invoice_line_items"
  | "match_supplier_document_lines"
  | "match_supplier_with_famous"
  | "notify"
  | "observe_data_source_link"
  | "upsert_product"
  | "upsert_inventory_snapshot"
  | "upsert_sales_order_line"
  | "upsert_customer"
  | "match_customer_with_evidence"
  | "upsert_purchase_order_line"
  | "upsert_transfer_order_line"
  | "upsert_supplier_product"
  | "upsert_supplier_warehouse_constraint"
  | "upsert_freight_code"
  | "upsert_supplier_sending_setup"
  | "upsert_warehouse_customer_account"
  | "upsert_purchase_order_release"
  | "upsert_warehouse_freight_default"
  | "upsert_cost_agreement"
  | "upsert_inventory_lot"
  | "upsert_product_supercede"
  | "upsert_sales_history_transfer"
  | "upsert_warehouse"
  | "upsert_warehouse_hierarchy"
  | "import_short_codes"
  | "upsert_inventory_policy_limits"
  | "upsert_inventory_policy_classification"
  | "upsert_vendor"
  | "upsert_vendor_rexel"
  | "parse_file"
  | "parse_edi"
  | "parse_pipe_self_describing_file"
  | "pipeline_rows_insert"
  | "pipeline_rows_process"
  | "pipeline_rows_query"
  | "pipeline_rows_update"
  | "platform_heartbeat"
  | "post_staged_transactions"
  | "post_849_review_scorecard"
  | "detect_as400_confirmations"
  | "poll_vdm_as400_responses"
  | "investigate_849_rejections"
  | "match_849_email"
  | "identify_849_email"
  | "attach_849_rejection_detail"
  | "prepare_collection_reminder"
  | "prepare_pricelist_case_files_ready_reminder"
  | "prepare_pricelist_case_reminder"
  | "prepare_summary_email"
  | "process_edi_849"
  | "process_edi_997"
  | "process_pick_ticket_bol"
  | "process_sd"
  | "query_due_pricelist_case_files_ready"
  | "query_due_pricelist_case_follow_ups"
  | "query_stale_collection_threads"
  | "random_number_generator"
  | "scan_payment_reminder_customers"
  | "scan_unsent_invoices"
  | "sync_tasks_from_rows"
  | "reassign_task"
  | "rebate_csv_extract_constants"
  | "rebate_csv_map_fields"
  | "rebate_csv_preprocess"
  | "rebate_csv_transform"
  | "refresh_pending_bpids"
  | "render_document_as_text"
  | "render_order_approval_email"
  | "render_order_notification_email"
  | "resolve_approval_recipients"
  | "resolve_entity_watchers"
  | "resolve_inbound_email_for_staged_purchase_order"
  | "resolve_invoice_references"
  | "detect_short_pay_from_remittance"
  | "detect_deductions_from_remittance"
  | "detect_remittance_collection_threads"
  | "build_remittance_verification_subject"
  | "persist_remittance_verification"
  | "parse_collection_filing_instruction"
  | "resolve_collection_filing_target"
  | "file_collection_evidence"
  | "file_collection_evidence_documents"
  | "read_portal_evidence_documents"
  | "send_collection_filing_receipt"
  | "match_collection_threads"
  | "detect_conversation_reply"
  | "suggest_conversation_reply"
  | "scan_conversations_awaiting_reply"
  | "run_rebate_forecast"
  | "run_replenishment_engine"
  | "run_demand_forecast"
  | "sync_item_groups_from_gains"
  | "sync_gains_vendor_catalog"
  | "release_auto_transfers"
  | "propose_supply_network_from_branch_master"
  | "release_draft_purchase_order"
  | "schedule_payment"
  | "schedule_trigger"
  | "send_announcement"
  | "send_announcement_v2"
  | "send_email"
  | "send_famous_invoice_to_orderful_810"
  | "send_famous_order_confirmation_to_orderful_855"
  | "send_famous_shipment_to_orderful_856"
  | "send_teams_notification"
  | "snooze_workflow_rerun"
  | "split_document_v2"
  | "split_documents"
  | "split_excel_sheets"
  | "split_excel_sheets_v4"
  | "unzip_document"
  | "unpack_zip_documents"
  | "split_staged_po"
  | "split_draft_sales_order"
  | "use_data_mapping"
  | "stateful_table_lookup_row"
  | "stateful_table_read_frame"
  | "stateful_table_write"
  | "submit_sales_orders"
  | "summarize_collection_threads"
  | "switch"
  | "sync_ap_invoice_to_famous"
  | "throw_error"
  | "transform_e844d_fields"
  | "transform_gsnetdef_policies"
  | "transform_initinfc_products"
  | "transform_initinfcs_products"
  | "transform_subchain_supercedes"
  | "transform_vendinfc_warehouse_constraints"
  | "transform_vendmast_suppliers"
  | "persist_spa_from_extraction"
  | "split_845_contracts"
  | "map_845_to_spa"
  | "upsert_spa_source"
  | "list_spa_sources"
  | "email_run_summary"
  | "spa_renewal_read_quotes"
  | "spa_renewal_read_as400_loads"
  | "spa_renewal_build_load_sheet"
  | "voice_agent"
  | "wait_for_event"
  | "web_browser"
  | "watch_for_file_changes"
  | "workflow_action"
  | "write_file_to_famous"
  | "write_supplier_product_matches";

export type ToolTypeId =
  | "agent.ap_invoice_update.add_line_item"
  | "agent.ap_invoice_update.fetch_thread_invoices"
  | "agent.ap_invoice_update.load_email_attachments"
  | "agent.ap_invoice_update.load_email_thread"
  | "agent.ap_invoice_update.load_invoice"
  | "agent.ap_invoice_update.remove_line_item"
  | "agent.ap_invoice_update.update_invoice_header"
  | "agent.ap_invoice_update.update_line_item"
  | "agent.collection_email_draft.get_customer_payment_history"
  | "agent.collection_email_draft.get_related_threads"
  | "agent.collection_email_draft.load_thread_context"
  | "agent.collection_evidence.file_portal_documents"
  | "file_collection_evidence"
  | "lookup_collection_threads"
  | "run_portal_action"
  | "agent.invoice_line_item_matching.arbitrate_matches"
  | "agent.invoice_line_item_matching.create_line_item_match"
  | "agent.invoice_line_item_matching.load_invoice_line_items"
  | "agent.invoice_line_item_matching.load_purchase_order"
  | "agent.invoice_line_item_matching.load_sales_order"
  | "agent.invoice_line_item_matching.load_shipment"
  | "agent.invoice_line_item_matching.load_supporting_documents"
  | "agent.invoice_line_item_matching.load_transfer_order"
  | "agent.invoice_line_item_matching.search_charges"
  | "agent.invoice_line_item_matching.search_po_line_items"
  | "agent.oms_email.create_email_draft"
  | "agent.oms_email.create_sales_order_draft"
  | "agent.oms_email.find_related_orders"
  | "agent.oms_email.find_validation_history"
  | "agent.oms_email.identify_sender"
  // Agent-harness AP read tools (backend/src/harness/modules/accounts-payable/tools)
  | "harness.ap.list_ap_invoices"
  | "harness.ap.get_ap_invoice"
  | "harness.ap.ap_invoice_metrics"
  | "harness.ap.search_suppliers"
  | "harness.ap.list_ap_payments"
  | "harness.ap.list_purchase_orders"
  // Agent-harness AR tools (backend/src/harness/modules/accounts-receivable)
  | "harness.ar.cash_metrics_query"
  | "harness.ar.search_ar_customers"
  | "harness.ar.ar_payment_timing"
  | "harness.ar.ar_aging"
  | "harness.ar.get_ar_customer_profile"
  | "harness.ar.list_ar_invoices"
  | "harness.ar.get_ar_invoice"
  | "harness.ar.list_ar_payments"
  | "harness.ar.list_ar_remittances"
  | "harness.ar.get_dashboard"
  | "harness.ar.add_widget"
  | "harness.ar.update_widget"
  | "harness.ar.remove_widget"
  | "harness.ar.move_widget"
  | "harness.ar.replace_layout"
  | "harness.ar.reset_to_template"
  | "harness.ar.request_remittance_portal_fetch"
  | "harness.rebates.resolve_rebate_scope"
  | "harness.rebates.search_rebate_agreements"
  | "harness.rebates.get_rebate_agreement"
  | "harness.rebates.list_rebate_charter_suppliers"
  | "harness.rebates.rebate_supplier_yoy"
  | "harness.rebates.rebate_tier_status"
  | "harness.rebates.rebate_volume"
  // Agent-harness OMS read tools (backend/src/harness/modules/oms/tools)
  | "harness.oms.list_sales_orders"
  | "harness.oms.sales_order_metrics"
  | "harness.oms.get_sales_order"
  | "harness.oms.get_sales_order_activity"
  | "harness.oms.search_customers"
  | "harness.oms.export_sales_orders_csv"
  | "harness.oms.discount_metrics"
  | "harness.oms.export_discounts_csv"
  | "harness.oms.search_products"
  | "harness.oms.product_pricing"
  | "harness.oms.product_margins"
  // Agent-harness OMS action tools (backend/src/harness/modules/oms/actions)
  | "harness.oms.update_line_price"
  | "harness.oms.update_line_quantity"
  | "harness.oms.update_line_fields"
  | "harness.oms.match_line_product"
  | "harness.oms.add_line"
  | "harness.oms.remove_line"
  | "harness.oms.update_header_fields"
  | "harness.oms.adjust_line_prices"
  | "harness.oms.apply_line_discounts"
  | "harness.oms.remove_line_discounts"
  | "harness.oms.create_bulk_discount_group"
  | "harness.oms.approve_order"
  | "harness.oms.reject_order"
  | "harness.oms.resolve_mismatch"
  | "harness.oms.retry_erp_submission"
  | "harness.oms.poll_erp_order_number"
  | "harness.oms.reassign_order"
  | "harness.oms.delete_order"
  | "harness.oms.create_discount_group"
  | "harness.oms.update_discount_group"
  | "harness.oms.add_discount_element"
  | "harness.oms.update_discount_element"
  | "harness.oms.delete_discount_element"
  | "harness.oms.deactivate_discount_element"
  | "harness.oms.submit_discount_group_for_approval"
  | "harness.oms.decide_discount_group"
  | "harness.oms.revert_discount_group_to_draft"
  | "harness.oms.deactivate_discount_group"
  | "harness.oms.delete_discount_group"
  | "harness.oms.resolve_discount_unresolved_line"
  | "harness.oms.dismiss_discount_unresolved_line"
  | "harness.oms.set_customer_price_list"
  | "harness.oms.activate_price_list_overlay"
  | "harness.oms.deactivate_price_list_overlay"
  | "harness.oms.rematch_price_list_overlay"
  | "harness.oms.create_customer_product_match"
  | "harness.oms.update_customer_product_match"
  | "harness.oms.delete_customer_product_match"
  | "agent.oms_email.load_email"
  | "agent.oms_email.load_email_attachments"
  | "agent.oms_email.load_email_thread"
  | "agent.oms_email.load_organization_context"
  | "agent.oms_email.load_staged_order"
  | "agent.oms_email.search_customer_orders"
  | "agent.oms_email.validate_inventory"
  | "agent.oms_email.validate_pricing"
  | "agent.cash_receipt_keying.load_work_item"
  | "agent.payment_review.load_context"
  | "agent.sd849_investigation.load_context"
  | "agent.sd849_investigation.investigate_transaction"
  | "agent.sd849_investigation.search_related"
  | "agent.sd849_email_matching.load_context"
  | "agent.sd849_email_matching.lookup_vdm"
  | "agent.sd849_email_matching.get_sent_lines"
  | "agent.sd849_email_matching.lookup_844_lines"
  | "agent.sd849_email_matching.list_supplier_submissions"
  | "agent.sd849_email_matching.search_submitted_lines"
  | "agent.otp.get_otp_code"
  | "agent.remote_desktop.close"
  | "agent.remote_desktop.execute"
  | "agent.remote_desktop.login_to_remote_desktop"
  | "agent.qa.swag_labs_store"
  | "agent.reconciliation.compare_values"
  | "agent.schema_extensions.list_extension_fields"
  | "agent.schema_extensions.set_extension_values"
  | "agent.session.generate_summary"
  | "agent.tasks.find_task_assignees"
  | "agent.tasks.get_next_task"
  | "agent.tasks.conclude_task"
  | "agent.tasks.get_task_context"
  | "agent.tasks.update_task_status"
  | "agent.web_browser.execute"
  | "agent.web_browser.open"
  | "agent.web_browser.close"
  | "list_entity_associations"
  | "list_external_system_references"
  | "list_stateful_tables"
  | "stateful_table_upsert_rows"
  | "shared.load_email_attachments"
  | "shared.load_email_thread";

export type ConfigTypeId = StepTypeId | ToolTypeId;

/**
 * Step configuration (metadata and schemas)
 */
export interface StepConfig<
  TConfig extends z.ZodType,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
> {
  type: ConfigTypeId;
  name: string;
  description: string;
  category: StepCategory;

  /**
   * Node library palette section (ERP module). When omitted, defaults to Platform.
   * Functional {@link category} is unchanged for badges and execution metadata.
   */
  libraryModule?: StepLibraryModule;

  // Zod schemas for validation
  inputSchema: TInput;
  outputSchema: TOutput;
  configSchema: TConfig;

  // Typed emissions - events this step can emit
  emits: TEmits;

  /**
   * Hard-coded, step-type-level fanout batching. Unlike an instance's
   * `config.batch`, this is fixed on the step type and is NOT part of the
   * editable config (not in `configSchema`), so it never appears in the node
   * config UI. Its presence flips the engine's fanout from single-array (which
   * cross-products multiple aligned arrays) to lockstep zip. The DataSource
   * Writers use `{ size: 1 }` to zip the adapter's aligned `blobStorageUris` +
   * `dataSourceLinks` into one (uri, link) pair per run.
   */
  batch?: WorkflowStepBatchConfig;

  /**
   * Hard-coded, step-type-level flag on a fanout SOURCE (a step whose array
   * outputs feed downstream single-value sockets). When set, the engine spawns
   * the per-item fanout children as DETACHED fire-and-forget child workflows:
   * each child gets its own Temporal execution timeout and the parent run
   * completes as soon as all children have started, instead of tracking every
   * item inside the parent's single execution-timeout budget. The parent does
   * NOT observe child failures — each child WorkflowRun records its own
   * status. Use for long-running per-item work (e.g. the Azure Blob DataSource
   * Adapter fanning N ingest loads out to DataSource Writers).
   */
  detachedFanout?: boolean;

  /**
   * Permissions required to execute this step/tool.
   *
   * Flat array with AND semantics.
   */
  requiredPermissions?: Permission[];

  // Temporal execution options
  execution?: {
    startToCloseTimeout?: string; // e.g., "5 minutes"
    scheduleToCloseTimeout?: string;
    // When set, Temporal reschedules the activity if no heartbeat is received
    // within this window — required to detect silent worker deaths (OOM,
    // SIGKILL during a deploy) in a bounded time instead of waiting for
    // startToCloseTimeout. Only meaningful for handlers that actually call
    // Context.current().heartbeat().
    heartbeatTimeout?: string;
    retry?: {
      maximumAttempts?: number;
      backoffCoefficient?: number;
      initialInterval?: string;
      maximumInterval?: string;
    };
    nonRetryableErrorTypes?: string[];
  };

  /**
   * Configure this step to wait for an external signal before executing.
   *
   * ⚠️ CAUTION: This is a powerful feature that should be used sparingly.
   * Steps with waitForSignal will pause the workflow until a signal arrives,
   * then execute repeatedly until `resolved: true` is returned.
   *
   * Requirements:
   * - Step handler MUST return `{ resolved: boolean, ... }` in its output
   * - Step should be idempotent (may execute multiple times per signal)
   * - Step should be lightweight (executes on every signal until resolved)
   */
  waitForSignal?: {
    /** Name of the signal to wait for */
    signalName: string;
    /** Timeout in milliseconds (default: 7 days) */
    timeoutMs?: number;
    /** Maximum iterations before failing (default: 50) */
    maxIterations?: number;
  };

  /**
   * Marks this step type as deprecated. Deprecated steps are hidden from the
   * node library palette and the AI agent catalog, but remain executable so
   * existing workflows keep working. The editor canvas shows a warning badge.
   */
  /**
   * Visibility predicates in disjunctive normal form (OR of AND groups).
   * When omitted the step is visible to everyone.
   * @see VisibilityRule
   */
  visibility?: VisibilityRule;

  deprecated?: {
    reason: string;
    replacedBy?: StepTypeId;
  };

  /**
   * Marks this step type as experimental. Experimental steps are visible and
   * usable but carry a badge indicating they are not yet ready for production
   * use. Use this for steps that are still being validated in real workflows.
   */
  experimental?: {
    reason: string;
  };

  /**
   * Tool catalog keys related to this step type. Used by the workflow manager
   * agent to assemble a merged tool set that covers the workflow's capabilities.
   */
  toolTypes?: string[];

  /**
   * Evaluation configuration for this step type.
   *
   * Defines which output fields are relevant for human evaluation.
   * When seeding test cases from history, only these fields are shown
   * and stored as expected output. Other output fields (like confidence,
   * reasoning, etc.) are treated as implementation details.
   */
  evalConfig?: {
    /** Output field keys the evaluator should compare against. */
    evalOutputKeys: string[];
    /**
     * How the step structures its output.
     * - `"parallel_arrays"`: Output keys are parallel arrays (one entry per item).
     *   The eval UI unpacks them into per-item rows for labeling.
     * - `undefined` / `"object"`: Output is a flat object (default).
     */
    outputFormat?: "parallel_arrays" | "object";
    /**
     * When `outputFormat` is `"parallel_arrays"`, additional output keys
     * to display alongside eval keys for context (e.g., document name).
     * These are shown in the UI but not stored in expectedOutput.
     */
    contextOutputKeys?: string[];
  };
}

export type ToolConfig<
  TConfig extends z.ZodType,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
> = StepConfig<TConfig, TInput, TOutput, TEmits>;

/**
 * Step handler function
 */
export type StepHandler<
  /* eslint-disable @typescript-eslint/no-explicit-any */
  TStepConfig extends StepConfig<any, any, any, any> = StepConfig<
    any,
    any,
    any,
    any
  >,
  /* eslint-enable @typescript-eslint/no-explicit-any */
> = (
  input: z.infer<TStepConfig["inputSchema"]>,
  config: z.infer<TStepConfig["configSchema"]>,
  context: StepExecutionContext<TStepConfig["emits"]>,
) => Promise<z.infer<TStepConfig["outputSchema"]>>;

/**
 * Optional batch handler. When a step registers one, the fanout-batch executor
 * (`executeStepBatch`) invokes it ONCE per chunk with every row's input, instead
 * of looping the per-item {@link StepHandler}. It must return one output per
 * input, index-aligned, so the per-item audit trail and the rejoined `array<T>`
 * output are preserved. Use it for steps that can collapse a chunk into a single
 * backing operation (e.g. one bulk DB write) rather than N per-row operations.
 *
 * A batch handler runs with a lean context (logger + lifecycle); guardrails,
 * emit, and per-item error isolation do NOT apply — the chunk succeeds or fails
 * as a unit (and retries, so the operation must be idempotent).
 */
export type StepBatchHandler<
  /* eslint-disable @typescript-eslint/no-explicit-any */
  TStepConfig extends StepConfig<any, any, any, any> = StepConfig<
    any,
    any,
    any,
    any
  >,
  /* eslint-enable @typescript-eslint/no-explicit-any */
> = (
  inputs: Array<z.infer<TStepConfig["inputSchema"]>>,
  config: z.infer<TStepConfig["configSchema"]>,
  context: StepExecutionContext<TStepConfig["emits"]>,
) => Promise<Array<z.infer<TStepConfig["outputSchema"]>>>;

export type ToolHandler<
  /* eslint-disable @typescript-eslint/no-explicit-any */
  TToolConfig extends ToolConfig<any, any, any, any> = ToolConfig<
    any,
    any,
    any,
    any
  >,
  /* eslint-enable @typescript-eslint/no-explicit-any */
> = (
  input: z.infer<TToolConfig["inputSchema"]>,
  config: z.infer<TToolConfig["configSchema"]>,
  context: ToolExecutionContext<TToolConfig["emits"]>,
) => Promise<z.infer<TToolConfig["outputSchema"]>>;

/**
 * Complete step implementation (config + handler)
 * This defines how a step type is implemented in code.
 *
 * Note: This is different from StepDefinition in types/index.ts,
 * which defines a step instance in a workflow graph (UI representation).
 */
export interface WorkflowStep<
  TConfig extends z.ZodType = z.ZodType<Record<string, never>>,
  TInput extends z.ZodType = z.ZodType,
  TOutput extends z.ZodType = z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
  TStepConfig extends StepConfig<TConfig, TInput, TOutput, TEmits> = StepConfig<
    TConfig,
    TInput,
    TOutput,
    TEmits
  >,
> {
  config: TStepConfig;
  handler: StepHandler<TStepConfig>;
  /** Optional bulk path for fanout batches; see {@link StepBatchHandler}. */
  batchHandler?: StepBatchHandler<TStepConfig>;
}

/**
 * Step registry for managing available workflow steps
 */
export class StepRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private steps = new Map<string, WorkflowStep<any, any, any, any>>();

  /**
   * Register a workflow step
   *
   * Can be called with either:
   * 1. A complete step (config + handler): register(step)
   * 2. A config and handler separately: register(stepConfig, handler)
   */
  public register<
    TConfig extends z.ZodType,
    TInput extends z.ZodType,
    TOutput extends z.ZodType,
    TEmits extends readonly SystemEvent[] = readonly [],
  >(
    config: StepConfig<TConfig, TInput, TOutput, TEmits>,
    handler: StepHandler<StepConfig<TConfig, TInput, TOutput, TEmits>>,
    batchHandler?: StepBatchHandler<
      StepConfig<TConfig, TInput, TOutput, TEmits>
    >,
  ): void {
    const finalStep: WorkflowStep<TConfig, TInput, TOutput, TEmits> = {
      config,
      handler,
      batchHandler,
    };

    if (this.steps.has(finalStep.config.type)) {
      throw new Error(`Step type ${finalStep.config.type} already registered`);
    }

    this.steps.set(finalStep.config.type, finalStep);
  }

  /**
   * Get a step by type
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(type: string): WorkflowStep<any, any, any, any> {
    const step = this.steps.get(type);
    if (!step) {
      throw new Error(`Step type ${type} not registered`);
    }
    return step;
  }

  /**
   * Get step config only (without handler)
   * Useful for UI/metadata purposes
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getConfig(type: string): StepConfig<any, any, any, any> {
    return this.get(type).config;
  }

  /**
   * List all registered steps
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  list(): WorkflowStep<any, any, any, any>[] {
    return Array.from(this.steps.values());
  }

  get size(): number {
    return this.steps.size;
  }

  /**
   * List steps by category
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listByCategory(category: StepCategory): WorkflowStep<any, any, any, any>[] {
    return this.list().filter((step) => step.config.category === category);
  }

  /**
   * Check if a step type is registered
   */
  has(type: string): boolean {
    return this.steps.has(type);
  }

  /**
   * Get all registered step types
   */
  getTypes(): string[] {
    return Array.from(this.steps.keys());
  }
}

/**
 * Global step registry instance
 */
export const stepRegistry = new StepRegistry();

// Export helpers
export * from "./helpers";
export * from "./visibility";
